import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, LayoutDashboard, LogOut, ShieldCheck, Sun, Moon } from "lucide-react";
import {
  SiInstagram, SiFacebook, SiTiktok, SiYoutube, SiX, SiSnapchat, SiTelegram,
  SiTwitch, SiThreads, SiDiscord, SiSoundcloud,
} from "react-icons/si";
import { FaLinkedinIn } from "react-icons/fa6";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useCurrency } from "../../contexts/CurrencyContext";
import { useTheme } from "../../contexts/ThemeContext";
import { getNetworkMeta } from "../../data/networksMeta";
import { Button } from "../ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const NETWORK_ICONS = {
  instagram: SiInstagram, facebook: SiFacebook, tiktok: SiTiktok, youtube: SiYoutube,
  twitter: SiX, snapchat: SiSnapchat, telegram: SiTelegram, linkedin: FaLinkedinIn,
  twitch: SiTwitch, threads: SiThreads, discord: SiDiscord, soundcloud: SiSoundcloud,
};

const NETWORK_LINKS = [
  ["instagram", "Instagram"], ["tiktok", "TikTok"], ["facebook", "Facebook"], ["youtube", "YouTube"],
  ["twitter", "X (Twitter)"], ["snapchat", "Snapchat"], ["telegram", "Telegram"],
  ["linkedin", "LinkedIn"], ["twitch", "Twitch"], ["threads", "Threads"], ["discord", "Discord"],
  ["soundcloud", "SoundCloud"],
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { currency, setCurrency } = useCurrency();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 glass" data-testid="navbar">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold" data-testid="nav-logo">
          <img src="/nav-icon-80.png" alt="Buy Likes and Followers" className="w-9 h-9 object-contain" width="36" height="36" />
          <span className="text-gradient hidden sm:inline">Buy Likes and Followers</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          <Link to="/" className="text-sm text-zinc-300 hover:text-white transition-colors" data-testid="nav-home-link">{t("nav.home", "Accueil")}</Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 text-sm text-zinc-300 hover:text-white transition-colors" data-testid="nav-services-trigger">
                {t("nav.services", "Services")} <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-white/10 grid grid-cols-2 gap-1 w-96 p-2">
              {NETWORK_LINKS.map(([slug, name]) => {
                const Icon = NETWORK_ICONS[slug];
                const meta = getNetworkMeta(slug);
                return (
                  <DropdownMenuItem key={slug} asChild data-testid={`nav-network-${slug}`}>
                    <Link to={`/reseaux/${slug}`} className="flex items-center gap-2.5 text-sm cursor-pointer">
                      <span
                        className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
                        style={{ background: `linear-gradient(135deg, ${meta.solid}33, ${meta.solid}0d)`, border: `1px solid ${meta.solid}40` }}
                      >
                        <Icon className="w-3.5 h-3.5" style={{ color: meta.solid }} />
                      </span>
                      {name}
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/blog" className="text-sm text-zinc-300 hover:text-white transition-colors" data-testid="nav-blog-link">{t("nav.blog", "Blog")}</Link>
          <Link to="/faq" className="text-sm text-zinc-300 hover:text-white transition-colors" data-testid="nav-faq-link">{t("nav.faq", "FAQ")}</Link>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
            aria-label={theme === "dark" ? t("nav.aria_switch_light", "Activer le mode clair") : t("nav.aria_switch_dark", "Activer le mode sombre")}
            className="relative w-14 h-8 rounded-full border border-white/10 bg-white/5 flex items-center px-1 transition-colors"
          >
            <span
              className="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center transition-transform"
              style={{ transform: theme === "dark" ? "translateX(24px)" : "translateX(0px)" }}
            >
              {theme === "dark" ? <Moon className="w-3.5 h-3.5 text-white" /> : <Sun className="w-3.5 h-3.5 text-white" />}
            </span>
          </button>
          <div className="flex items-center rounded-full border border-white/10 overflow-hidden">
            <button onClick={() => setLanguage("fr")} data-testid="lang-switch-fr" aria-label="Français"
              className={`px-2.5 py-1.5 text-sm transition-colors ${language === "fr" ? "bg-indigo-500 text-white" : "text-zinc-400 hover:text-white"}`}>🇫🇷</button>
            <button onClick={() => setLanguage("en")} data-testid="lang-switch-en" aria-label="English"
              className={`px-2.5 py-1.5 text-sm transition-colors ${language === "en" ? "bg-indigo-500 text-white" : "text-zinc-400 hover:text-white"}`}>🇬🇧</button>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-sm text-zinc-300 hover:text-white px-2 py-1.5 border border-white/10 rounded-full" data-testid="currency-switch-trigger">{currency}</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-white/10">
              {["EUR", "USD"].map((c) => (
                <DropdownMenuItem key={c} onClick={() => setCurrency(c)} data-testid={`currency-option-${c}`}>{c}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {!user && (
            <>
              <Link to="/login"><Button variant="ghost" size="sm" data-testid="nav-login-btn">{t("nav.login", "Connexion")}</Button></Link>
              <Link to="/register"><Button size="sm" className="rounded-full bg-indigo-500 hover:bg-indigo-600" data-testid="nav-register-btn">{t("nav.register", "Créer un compte")}</Button></Link>
            </>
          )}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-9 h-9 rounded-full bg-indigo-500 text-white flex items-center justify-center text-sm font-semibold" data-testid="nav-user-menu-trigger">
                  {user.name?.[0]?.toUpperCase() || "U"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-zinc-900 border-white/10 w-48">
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin-link"><ShieldCheck className="w-4 h-4 mr-2" />{t("nav.admin", "Administration")}</DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-link"><LayoutDashboard className="w-4 h-4 mr-2" />{t("nav.dashboard", "Mon compte")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout-btn"><LogOut className="w-4 h-4 mr-2" />{t("nav.logout", "Déconnexion")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <button className="lg:hidden" onClick={() => setOpen(!open)} aria-label={open ? t("nav.aria_close_menu", "Fermer le menu") : t("nav.aria_open_menu", "Ouvrir le menu")} data-testid="nav-mobile-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 px-4 py-4 space-y-3" data-testid="nav-mobile-menu">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-btn-mobile"
            className="flex items-center gap-2 text-sm text-zinc-300"
          >
            {theme === "dark" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            {theme === "dark" ? t("nav.dark_mode", "Mode sombre") : t("nav.light_mode", "Mode clair")}
          </button>
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-full border border-white/10 overflow-hidden">
              <button onClick={() => setLanguage("fr")} data-testid="lang-switch-fr-mobile" aria-label="Français"
                className={`px-3 py-1.5 text-sm transition-colors ${language === "fr" ? "bg-indigo-500 text-white" : "text-zinc-400"}`}>🇫🇷 FR</button>
              <button onClick={() => setLanguage("en")} data-testid="lang-switch-en-mobile" aria-label="English"
                className={`px-3 py-1.5 text-sm transition-colors ${language === "en" ? "bg-indigo-500 text-white" : "text-zinc-400"}`}>🇬🇧 EN</button>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-sm text-zinc-300 px-3 py-1.5 border border-white/10 rounded-full" data-testid="currency-switch-trigger-mobile">{currency}</button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-zinc-900 border-white/10">
                {["EUR", "USD"].map((c) => (
                  <DropdownMenuItem key={c} onClick={() => setCurrency(c)} data-testid={`currency-option-mobile-${c}`}>{c}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Link to="/" onClick={() => setOpen(false)} className="block text-sm text-zinc-300">{t("nav.home", "Accueil")}</Link>
          <Link to="/blog" onClick={() => setOpen(false)} className="block text-sm text-zinc-300">{t("nav.blog", "Blog")}</Link>
          <Link to="/faq" onClick={() => setOpen(false)} className="block text-sm text-zinc-300">{t("nav.faq", "FAQ")}</Link>
          <div className="grid grid-cols-2 gap-2 pt-2">
            {NETWORK_LINKS.map(([slug, name]) => {
              const Icon = NETWORK_ICONS[slug];
              const meta = getNetworkMeta(slug);
              return (
                <Link key={slug} to={`/reseaux/${slug}`} onClick={() => setOpen(false)} className="flex items-center gap-2 text-xs text-zinc-400" data-testid={`nav-mobile-network-${slug}`}>
                  <span
                    className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: `linear-gradient(135deg, ${meta.solid}33, ${meta.solid}0d)`, border: `1px solid ${meta.solid}40` }}
                  >
                    <Icon className="w-3 h-3" style={{ color: meta.solid }} />
                  </span>
                  {name}
                </Link>
              );
            })}
          </div>
          <div className="flex gap-2 pt-3">
            {!user ? (
              <>
                <Link to="/login" className="flex-1"><Button variant="outline" className="w-full">{t("nav.login", "Connexion")}</Button></Link>
                <Link to="/register" className="flex-1"><Button className="w-full bg-indigo-500">{t("nav.register", "Créer un compte")}</Button></Link>
              </>
            ) : (
              <Link to="/dashboard" className="flex-1"><Button className="w-full bg-indigo-500">{t("nav.dashboard", "Mon compte")}</Button></Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
